import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  ToggleButton,
  ToggleButtonGroup,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { api } from '../../services/api';
import ReportContent from '../ReportContent';
import { mapServerResponsesToLocal, pollResearchStatus } from '../../services/apiUtils';
import { ThemeProvider } from '@mui/material/styles';
import ResearchTheme from '../../styles/Theme.js';
import PieChartComponent from '../../components/PieChartComponent';
import BarChartComponent from '../../components/BarChartComponent';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { paymentApi } from '../../services/paymentApi';
import { useAuth } from '../../authentication/AuthContext';
import { useCallback } from 'react';

function Step3Responses({ userData, setUserData, onTopicsChange, setTabIndex }) {
  const navigate = useNavigate();
  const { isAdmin, updateUserAttributes } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('byQuestion'); 
  const [pollingFailed, setPollingFailed] = useState(false);

  const [expandedPanel, setExpandedPanel] = useState(0);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [expandedPersonaPanels, setExpandedPersonaPanels] = useState({});
  const [showAdvancedAnalysis, setShowAdvancedAnalysis] = useState({});
  const [hasToggled, setHasToggled] = useState(false);
  const [groupByAttributes, setGroupByAttributes] = useState({});

  const [loadingProgress, setLoadingProgress] = useState(0);

// Add to your imports (you already have useEffect)
// No new imports needed

// In the Step3Responses component:
const [animatedProgress, setAnimatedProgress] = useState(0);

// Update in client/src/research/ResponsesStep/Step3Responses.js
const handleGenerateResponses = useCallback(async () => {
  console.log("Generating responses for user:", userData.userID, "researchID:", userData.researchID);
  setLoading(true);
  setError(null);
  setPollingFailed(false);
  
  // Flag to track if we've used a credit
  let creditUsed = false;
  try {
   
    // Only check for credits if NOT an admin
    if (!isAdmin) {
      // Check for credits before proceeding
      const creditResponse = await paymentApi.useCredit();

    
      if (!creditResponse.success) {
        setError(creditResponse.message || 'Insufficient credits to generate responses.');
        
        if (creditResponse.redirectToTopUp) {
          navigate('/account', { 
            state: { 
              message: 'You need credits to generate responses. Please top up your wallet.' 
            } 
          });
        }
      
        setLoading(false);
        return;
      }
    
      // Mark that we've successfully used a credit
      creditUsed = true;
      
      // Update the user's credit count in the local state
      if (updateUserAttributes) {
        await updateUserAttributes();
      }
    }
    
    // Proceed with response generation
    await api.generateResponses(
      userData.userID,
      userData.researchID
    );

    // Important: clear the surveyResponses AND set status to PENDING
    setUserData(prev => ({
      ...prev,
      responsesStatus: 'PENDING',
      surveyResponses: null,
      lastGenerationState: {
        questionCount: prev.surveyQuestions?.length || 0,
        personaCount: Object.keys(prev.selectedPersonas || {}).length,
        questions: prev.surveyQuestions?.map(q => ({
          questionID: q.questionID,
          questionText: q.questionText,
          questionType: q.questionType,
          responseOptions: q.responseOptions
        }))
      },
      hasChangedSinceLastGeneration: false
    }));

    if (onTopicsChange) {
      onTopicsChange();
    }
          
  } catch (err) {
    setError(err.message);
    
    // If we used a credit but the generation failed, refund the credit
    if (creditUsed) {
      try {
        console.log("Refunding credit due to failed generation");
        const refundResponse = await paymentApi.refundCredit();
        
        if (refundResponse.success) {
          console.log("Credit refunded successfully");
          
          // Update the user's credit count in the local state
          if (updateUserAttributes) {
            await updateUserAttributes();
          }
          
          // Append to the error message that credit was refunded
          setError(`${err.message} (Your credit has been refunded)`);
        } else {
          // console.error("Failed to refund credit:", refundResponse);
        }
      } catch (refundError) {
        // console.error("Error refunding credit:", refundError);
      }
    }
    
    setLoading(false);
  }
}, [userData.userID, userData.researchID, isAdmin, updateUserAttributes, navigate, onTopicsChange, setUserData]);

// Effect to update animated progress continuously
useEffect(() => {

   console.log("UPDATE ANIMATED PROGRESS, LOADINGPROGRESS IS",loadingProgress);
   

  // Only run animation if loading
  if ((userData.responsesStatus === 'PENDING' && !userData.surveyResponses) || loading) {

    // Set up animation interval
    const interval = setInterval(() => {
      setAnimatedProgress(prev => {
        // Calculate current speed - faster at start and end, slower in the middle
        let speed = 0.005; // Base speed increment per interval
        if (prev < 10) {
          speed = 0.1
        } else if (prev < 20) {
          // Fast at the beginning (0-20%)
          speed = 0.05;
        } 
        
        // Move toward target progress but don't exceed it
        
        return Math.min(100,Math.max(speed + prev, loadingProgress));
      });
    }, 50); // Update every 50ms for smooth animation
    
    // Clean up interval
    return () => clearInterval(interval);
  } else {
    // Reset animation when not loading
    setAnimatedProgress(0);
  }
}, [userData.responsesStatus, loading, loadingProgress, userData.surveyResponses]);

  const handleAccordionChange = (panel) => (_, isExpanded) => {
    setExpandedPanel(isExpanded ? panel : null);
    setHasToggled(false);
  };

  const toggleExpandQuestion = (index) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleExpandPersona = (id) => {
    setExpandedPersonaPanels((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  // TODO: may be used for advanced analysis
  //const handleShowAnalysis = () => {
  //  setHasToggled(true);
  //  setShowAdvancedAnalysis((prev) => !prev);
  //};

// Add polling effect for response generation
useEffect(() => {
  console.log("JUST ARRIVED IN STEP 3!!!!!!!!!!!!! RIGHT NOW USERDATA IS: ", userData);
  const targetIteration = Object.keys(userData.selectedPersonas).length;
  console.log("targetIteration",targetIteration);
  
  // Check if we should start polling for responses
  if (userData.researchID && userData.responsesStatus === 'PENDING' && !pollingFailed) {
    console.log("Polling for responses...");

    const pollForResponses = async () => {
      try {
        
        const research = await pollResearchStatus(userData.userID, userData.researchID, 'responsesStatus', (progress) => {setLoadingProgress(progress);}, targetIteration);
        console.log("successfully polled research", research);
        if (research) {
          const responsesData = await api.getExistingResponses(userData.userID, userData.researchID);
          console.log("responses after polling", responsesData);

          const responses = mapServerResponsesToLocal(responsesData.responses, userData.researchID);
          console.log("responses after formatting", responses);
          
          setUserData(prev => ({
            ...prev,
            surveyResponses: responses,
            responsesStatus: 'COMPLETED',
            questionResponsePending: null // Clear any pending question
          }));
          
          // Make sure to clear the loading state
          setLoading(false);
        }
      } catch (err) {
        setError(`Error polling for responses: ${err.message}`);
        setPollingFailed(true);
        setUserData(prev => ({
          ...prev,
          responsesStatus: 'ERROR'
        }));
        
        // Refund credit when polling fails
        try {
          console.log("Refunding credit due to failed response polling");
          const refundResponse = await paymentApi.refundCredit();
          
          if (refundResponse.success) {
            console.log("Credit refunded successfully");
            
            // Update the user's credit count in the local state
            if (updateUserAttributes) {
              await updateUserAttributes();
            }
            
            // Append to the error message that credit was refunded
            setError(`Error polling for responses: ${err.message} (Your credit has been refunded)`);
          } else {
            // console.error("Failed to refund credit:", refundResponse);
          }
        } catch (refundError) {
          // console.error("Error refunding credit:", refundError);
        }
        
        setLoading(false);
      }
    };
    pollForResponses();
  }

  // Instead of automatically generating responses, check if regeneration is needed
  if (userData.responsesStatus === 'REGENERATE_NEEDED' && !pollingFailed) {
    // User has confirmed regeneration in the previous step
    console.log("Regeneration needed, starting generation immediately");
    handleGenerateResponses();
  }
  // Do NOT automatically generate for NOT_STARTED status anymore
}, [userData.responsesStatus, pollingFailed, userData.userID, userData.researchID, setUserData, handleGenerateResponses, updateUserAttributes, userData]);

  const handleContinue = async () => {
    // Switch to Responses tab
    setTabIndex(3);
  };

  const groupResponsesByAttribute = (responses, personas, attribute) => {
    const result = {};
    const allGroups = new Set();

    console.log("Grouping responses by attribute:", responses, personas, attribute);

    responses.forEach(({ response, personaID }) => {
      const attrValue = personas[personaID]?.[attribute] || 'Unknown';
      const options = Array.isArray(response) ? response : [response];

      allGroups.add(attrValue);

      options.forEach(option => {
        if (!result[option]) result[option] = { option };
        result[option][attrValue] = (result[option][attrValue] || 0) + 1;
      });
    });

    const groups = Array.from(allGroups);
    Object.values(result).forEach(obj => {
      groups.forEach(group => {
        if (!(group in obj)) obj[group] = 0;
      });
    });

    console.log("Grouped responses by attribute:", attribute, result);

    return Object.values(result);
  };

  const handleGroupByChange = (questionID, value) => {
    setGroupByAttributes(prev => ({
      ...prev,
      [questionID]: value
    }));
  };

  const handleToggleAdvancedAnalysis = (questionID) => {
    setShowAdvancedAnalysis(prev => ({
      ...prev,
      [questionID]: !prev[questionID]
    }));
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const organizeByQuestion = () => {
    if (!userData.surveyResponses) return [];
    
    console.log('Survey Questions:', userData.surveyQuestions);
    console.log('Results:', userData.surveyResponses);
    
    // Get persona IDs from the map keys
    const personaIds = userData.selectedPersonas ? Object.keys(userData.selectedPersonas) : [];
    console.log('Selected persona ids: ', personaIds);

    return userData.surveyQuestions.map((question, index) => {
      const questionResponses = userData.surveyResponses.filter(
        r => r.questionID === question.questionID && personaIds.includes(r.personaID.toString())
      );

      console.log(`Responses for question ${question.questionID}:`, questionResponses);

      return {
        question: {
          ...question,
          displayNumber: index + 1 // Add display number for "Question 1, 2, 3" etc.
        }, 
        responses: questionResponses
      };
    });
  };

  const organizeByPersona = () => {
    if (!userData.surveyResponses) return [];
    
    console.log("researchID from userData:", userData.researchID);
    console.log("userData.surveyResponses: ", userData.surveyResponses);
    
    // Map through the entries of selectedPersonas instead of the array
    return Object.entries(userData.selectedPersonas || {}).map(([personaId, persona]) => {
      const personaResponses = userData.surveyResponses.filter(
        r => r.personaID.toString() === personaId.toString() && r.researchID === userData.researchID
      );

      // Order the responses according to the question order
      const orderedResponses = userData.surveyQuestions.map(question => {
        const response = personaResponses.find(r => r.questionID === question.questionID);
        return response;
      }).filter(Boolean); // Remove undefined responses

      console.log(`Responses for persona ${personaId}:`, personaResponses);

      return {
        persona,
        personaId,
        responses: orderedResponses
      };
    });
  };
  
  const renderResponsesByQuestion = () => {
    const organizedData = organizeByQuestion();
    
    return (
      <Box>
        {organizedData.map((item, index) => {
          const { question } = item;  // Destructuring to get question object
          const questionId = question.questionID;  // Use correct property name
          const hasResponses = item.responses && item.responses.length > 0;
          //const isAnyQuestionRegenerating = !!userData.questionResponsePending;

          const isExpanded = expandedQuestions[index] || false;
          const hasMore = item.responses.length > 5;
          const displayedResponses = isExpanded ? item.responses : item.responses.slice(0, 5);

          // If no responses, display a simpler non-accordion view
          if (!hasResponses) {
            return (
              <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Question {item.question.displayNumber || index + 1}: {item.question.questionText}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No responses yet for this question.
                </Typography>
              </Paper>
            );
          }

          // Prepare response counts for the pie chart
          const responseCounts = item.responses.reduce((acc, response) => {
            if (Array.isArray(response.response)) {
              response.response.forEach((ans) => {
                const found = acc.find((r) => r.option === ans);
                if (found) {
                  found.count += 1;
                } else {
                  acc.push({ option: ans, count: 1 });
                }
              });
            } else if (typeof response.response === 'string') {
              const found = acc.find((r) => r.option === response.response);
              if (found) {
                found.count += 1;
              } else {
                acc.push({ option: response.response, count: 1 });
              }
            }
            return acc;
          }, []);
          
          // For questions with responses, use the accordion view
          return (
            <Accordion
              key={index}
              expanded={expandedPanel === index}
              onChange={handleAccordionChange(index)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center' }}>
                    Question {item.question.displayNumber || index + 1}: {item.question.questionText}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box sx={{ mb: 1 }}></Box>

                {item.question.questionType === 'multiple-choice' && (
                  expandedPanel === index && (
                    <Paper sx={{p: 3, mt: 4, mb: 4, boxShadow: 1, borderRadius: 2, overflow: 'hidden'}}>
                      <Box sx={{ position: 'relative', height: 450, overflow: 'hidden' }}>
                        <AnimatePresence mode="wait">
                          {!showAdvancedAnalysis[questionId] ? (
                            <motion.div
                              key="pie"
                              initial={{ x: 300, opacity: 0, scale: 0.8 }}
                              animate={{ x: 0, opacity: 1, scale: 1 }}
                              exit={{ x: -300, opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.4 }}
                            >
                              <PieChartComponent
                                questionText={item.question.questionText}
                                responseCounts={responseCounts}
                                disableAnimation={hasToggled}
                              />
                            </motion.div>
                          ) : (
                            <motion.div
                              key="bar"
                              initial={{ x: 300, opacity: 0, scale: 0.8 }}
                              animate={{ x: 0, opacity: 1, scale: 1 }}
                              exit={{ x: 300, opacity: 0, scale: 0.8 }}
                              transition={{ duration: 0.4 }}
                            >
                              <BarChartComponent 
                                questionText={item.question.questionText}
                                groupedResponses={groupResponsesByAttribute(
                                  item.responses,
                                  userData.selectedPersonas,
                                  groupByAttributes[questionId] || 'gender'
                                )}
                                attribute={groupByAttributes[questionId] || 'gender'}
                              />
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </Box>
                      
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          mt: 2 
                        }}
                      >
                        <Button
                          size="small"
                          variant="contained"
                          color="primary"
                          startIcon={showAdvancedAnalysis[questionId] ? <ArrowBackIcon /> : null}
                          endIcon={showAdvancedAnalysis[questionId] ? null : <ArrowForwardIcon />}
                          onClick={() => handleToggleAdvancedAnalysis(questionId)}
                        >
                          {showAdvancedAnalysis[questionId]
                            ? 'Back to Basic Analysis'
                            : 'See More Advanced Analysis'}
                        </Button>

                        {showAdvancedAnalysis[questionId] && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">Group responses by:</Typography>
                            <TextField
                              select
                              label="Attribute"
                              value={groupByAttributes[questionId] || 'gender'}
                              onChange={(e) => handleGroupByChange(questionId, e.target.value)}
                              size="small"
                              sx={{ minWidth: 200 }}
                            >
                              <MenuItem value="gender">Gender</MenuItem>
                              <MenuItem value="age_range">Age</MenuItem>
                              <MenuItem value="employment_status">Employment</MenuItem>
                              <MenuItem value="income">Income</MenuItem>
                              <MenuItem value="education">Education</MenuItem>
                              <MenuItem value="household_situation">Household</MenuItem>
                              <MenuItem value="marital_status">Marital</MenuItem>
                            </TextField>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  )
                )}
                
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Persona</TableCell>
                        <TableCell>Response</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {displayedResponses.map((response, rIndex) => {
                        const persona = userData.selectedPersonas?.[response.personaID];
                        return (
                          <TableRow key={rIndex}>
                            <TableCell>
                              {persona?.name || `Persona ${parseInt(response.personaID)}`}
                            </TableCell>
                            <TableCell>
                              {Array.isArray(response.response) ? (
                                response.response.map((ans, i) => (
                                  <React.Fragment key={i}>
                                    {ans}
                                    {i < response.response.length - 1 && <br />}
                                  </React.Fragment>
                                ))
                              ) : (
                                response.response
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  {hasMore && (
                    <Button onClick={() => toggleExpandQuestion(index)} sx={{ mt: 2 }}>
                      {isExpanded ? 'Show Less' : 'Show More'}
                    </Button>
                  )}
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>
    );
  };
  
  const renderResponsesByPersona = () => {
    const organizedData = organizeByPersona();
    
    return (
      <Box>
        {organizedData.map((item, index) => { 
          const personaId = item.personaId;
          const isExpanded = expandedPersonaPanels[personaId] || false;

          return (
          <Accordion
            key={index}
            expanded={isExpanded}
            onChange={() => toggleExpandPersona(personaId)}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">
                {item.persona.name || `Persona ${parseInt(item.personaId)}`} ({item.persona.age_range}, {item.persona.gender})
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Question</TableCell>
                      <TableCell>Response</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {item.responses.map((response, rIndex) => {
                      const questionIndex = userData.surveyQuestions.findIndex(q => q.questionID === response.questionID);
                      const question = userData.surveyQuestions.find(q => q.questionID === response.questionID);
                      
                      return (
                        <TableRow key={rIndex}>
                          <TableCell>
                            Question {questionIndex + 1}: {question?.questionText || response.questionID}
                          </TableCell>
                          <TableCell>
                            {Array.isArray(response.response) ? (
                              response.response.map((ans, i) => (
                                <React.Fragment key={i}>
                                  {ans}
                                  {i < response.response.length - 1 && <br />}
                                </React.Fragment>
                              ))
                            ) : typeof response.response === 'string' ? (
                              response.response.split('\\n').map((line, i) => (
                                <React.Fragment key={i}>
                                  {line}
                                  {i < response.response.split('\\n').length - 1 && <br />}
                                </React.Fragment>
                              ))
                            ) : (
                              <>{JSON.stringify(response.response)}</>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        );
      })}
      </Box>
    );
  };

  // Determine which view to show based on the three cases you specified
  const renderContent = () => {
    const hasResponses = userData.surveyResponses && userData.surveyResponses.length > 0;

    // CASE 2: Responses are being generated (status is PENDING and no responses yet)
    // Also include the global loading state here
    if ((userData.responsesStatus === 'PENDING' && !hasResponses) || loading) {
      return (
<Paper sx={{ p: 4, mb: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
      {/* Background circle (always visible) */}
      <CircularProgress
        variant="determinate"
        value={100}
        size={150}
        thickness={4}
        sx={{ color: 'rgba(0, 0, 0, 0.1)' }}
      />
      
      {/* Foreground progress circle */}
      <CircularProgress
        variant="determinate"
        value={animatedProgress}
        size={150}
        thickness={4}
        sx={{
          position: 'absolute',
          left: 0,
        }}
      />
      
      {/* Percentage text in the center */}
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          sx={{ fontWeight: 'bold' }}
        >
          {`${Math.round(animatedProgress)}%`}
        </Typography>
      </Box>
    </Box>
    
    <Typography variant="h6" sx={{ mt: 2 }}>
      Generating responses...
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      This typically takes about 5 minutes to complete. You can stay on this page to see the responses as soon as they're ready, or come back later.
    </Typography>
  </Box>
</Paper>




      );
    }
    
    // CASE 1 & 3: We have responses
    if (hasResponses) {
      return (
        <Box>
          <Box sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
            >
              <ToggleButton value="byQuestion" aria-label="by question">
                View by Question
              </ToggleButton>
              <ToggleButton value="byPersona" aria-label="by persona">
                View by Persona
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {viewMode === 'byQuestion' ? renderResponsesByQuestion() : renderResponsesByPersona()}
        </Box>
      );
    }
    
    // If we don't have responses and not in PENDING state - show empty state with generate button
    return (
      <Paper sx={{ p: 4, mb: 2, textAlign: 'center', bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          No responses have been generated yet
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Click the "Generate New Responses" button below to generate responses for all questions from your selected personas.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleGenerateResponses}
            disabled={loading}
            size="large"
          >
            {loading ? <CircularProgress size={24} /> : 'Generate New Responses'}
          </Button>
        </Box>
      </Paper>
    );
  };

  return (
    <ThemeProvider theme={ResearchTheme}>
      <Box>
        <Typography variant="h5" gutterBottom>
          Generate Responses
        </Typography>

        <Paper sx={{ p: 3, mt: 2 }}>
          {(userData.responsesStatus === 'NOT_STARTED' || userData.responsesStatus === null) && (
            <Alert 
              severity="info" 
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleGenerateResponses}
                  disabled={loading}
                >
                  Generate Now
                </Button>
              }
              sx={{ mb: 2 }}
            >
              Click the button to generate responses from your personas based on your survey. This will use a research credit.
            </Alert>
          )}

          {(userData.surveyResponses && userData.surveyResponses.length > 0 && 
            userData.hasChangedSinceLastGeneration) && (
            <Alert 
              severity="warning" 
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleGenerateResponses}
                  disabled={loading}
                >
                  Regenerate
                </Button>
              }
              sx={{ mb: 2 }}
            >
              There have been changes to your survey or personas since you last generated responses. 
              Regenerating will use a research credit.
            </Alert>
          )}
          {renderContent()}
        </Paper>
        
        {/* Only show action buttons if we have responses and status is COMPLETED */}
        {userData.surveyResponses && userData.responsesStatus === 'COMPLETED' && !loading && (
          <Box sx={{ mt: 2 }}>
          </Box>
        )}
        
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            Error: {error}
          </Typography>
        )}
        
        <div style={{ display: 'none' }}>
          <ReportContent userData={userData} />
        </div>

      {/* Only show Continue button if we have responses and status is COMPLETED */}
      {userData.surveyResponses && userData.responsesStatus === 'COMPLETED' && (
        <Button
          variant="contained"
          onClick={handleContinue}
          sx={{ mt: 2, display: 'block', mx: 'auto' }}
        >
          Continue
        </Button>
      )}
      </Box>
    </ThemeProvider>
  );
}

export default Step3Responses;
